// src/modals/PrelegentEditorModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faGlobe } from "@fortawesome/free-solid-svg-icons";
import { faFacebook, faInstagram, faLinkedin, faYoutube } from "@fortawesome/free-brands-svg-icons";

/* ------------------------- CONST ------------------------- */
const API = "https://api18081113.queroapp.pl";

const FALLBACK_BG =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="1200" height="300" viewBox="0 0 1200 300">
      <defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stop-color="#e5e7eb"/><stop offset="100%" stop-color="#d1d5db"/>
      </linearGradient></defs>
      <rect width="1200" height="300" fill="url(#g)"/>
    </svg>`
  );

/* ------------------------- LOG ------------------------- */
const log = (...a) => console.log("%c[PEM]", "color:#7c3aed;font-weight:700;", ...a);
const warn = (...a) => console.warn("%c[PEM]", "color:#f59e0b;font-weight:700;", ...a);
const err = (...a) => console.error("%c[PEM]", "color:#ef4444;font-weight:700;", ...a);

async function fetchJson(url, init = {}) {
  const method = (init.method || "GET").toUpperCase();
  const t0 = performance.now();
  const res = await fetch(url, { mode: "cors", credentials: "omit", ...init });
  const text = await res.text().catch(() => "");
  let json = null;
  try { json = JSON.parse(text); } catch { }
  const ms = Math.round(performance.now() - t0);

  console.groupCollapsed(
    `%c[PEM][HTTP] ${method} ${url} → ${res.status} (${ms}ms)`,
    "color:#7c3aed;font-weight:700;"
  );
  if (init.headers) console.debug("headers →", init.headers);
  if (init.body && method !== "GET") {
    if (init.body instanceof FormData) {
      const fdSummary = {};
      init.body.forEach((v, k) => { fdSummary[k] = (v instanceof File) ? `[File:${v.name} ${v.size}B]` : String(v).slice(0, 300); });
      console.debug("body(FormData) →", fdSummary);
    } else if (typeof init.body === "string") {
      console.debug("body(string) →", init.body.slice(0, 1000));
    } else {
      console.debug("body →", init.body);
    }
  }
  console.info("status:", res.status, "ok:", res.ok);
  console.debug("response(text preview) ←", text.slice(0, 1000));
  if (json) console.debug("response(json) ←", json);
  console.groupEnd();

  return { res, json, text };
}

/* ------------------------- HELPERS ------------------------- */
const isFullLike = (name, sec) => {
  if (!name || !sec) return false;
  return name.includes(`_${sec}_`) && /_[A-Za-z0-9]+$/.test(name);
};

const toShort = (full, sec) =>
  isFullLike(full, sec) ? String(full).split(`_${sec}_`)[0].replace(/_/g, " ") : String(full || "").replace(/_/g, " ");

const toShortUnderscore = (full, sec) => toShort(full, sec).trim().replace(/\s+/g, "_");

const stripDiacritics = (s) => String(s || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "");
const toKey = (s) =>
  stripDiacritics(String(s || ""))
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^a-zA-Z0-9_]/g, "_")
    .replace(/^_+|_+$/g, "")
    .toLowerCase();

/** Resolver pełnej nazwy segmentu (dociąga stageId) z backendu */
async function resolveFullSegmentName(secretId, incomingShortOrFull) {
  const name = String(incomingShortOrFull || "").trim();
  if (!secretId || !name) return "";
  if (isFullLike(name, secretId)) return name;

  const shortKey = toKey(name);
  if (!shortKey) return "";

  // 1) /list-segments
  try {
    const { res, json } = await fetchJson(`${API}/list-segments`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ secretId }),
    });
    if (res.ok && Array.isArray(json?.data)) {
      for (const r of json.data) {
        const full = r?.full || r?.segment_name || r?.name || "";
        if (isFullLike(full, secretId)) {
          const stage = full.split(`_${secretId}_`)[0];
          if (toKey(stage) === shortKey) return full;
        }
      }
    }
  } catch { }

  // 2) /segments/:secretId
  try {
    const { res, json } = await fetchJson(`${API}/segments/${encodeURIComponent(secretId)}`);
    const list = Array.isArray(json) ? json : (json?.segments || json?.data || []);
    for (const r of list) {
      const full = typeof r === "string"
        ? r
        : (r?.segment_name || r?.segment || r?.full || r?.name || "");
      if (isFullLike(full, secretId)) {
        const stage = full.split(`_${secretId}_`)[0];
        if (toKey(stage) === shortKey) return full;
      }
    }
  } catch { }

  // 3) /event/get-all-stage-data
  try {
    const { res, json } = await fetchJson(`${API}/event/get-all-stage-data`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ secretId }),
    });
    const strings = [];
    const pushIf = (v) => {
      if (typeof v === "string") strings.push(v);
      else if (v != null && (typeof v === "number" || typeof v === "boolean")) strings.push(String(v));
    };
    const walk = (x) => {
      if (Array.isArray(x)) x.forEach((v) => (typeof v === "object" ? walk(v) : pushIf(v)));
      else if (x && typeof x === "object") Object.values(x).forEach((v) => (typeof v === "object" ? walk(v) : pushIf(v)));
    };
    if (res.ok) walk(json);
    const cand = strings.find(
      (s) => isFullLike(s, secretId) && toKey(s.split(`_${secretId}_`)[0]) === shortKey
    );
    if (cand) return cand;
  } catch { }

  return "";
}

const normalizeMedia = (val, kind /* "img"|"bg" */) => {
  if (val == null) return "";
  let s = String(val).trim();
  if (!s) return "";
  if (/^https?:\/\//i.test(s)) return s;
  if (s.includes("/")) {
    s = s.replace(/^\/+/, "");
    return `https://eventicon.queroapp.pl/${s}`;
  }
  const subdir = kind === "img" ? "prelegent_photo" : "prelegent_bg";
  return `https://eventicon.queroapp.pl/${subdir}/${s}`;
};

const pickSocial = (raw) => {
  if (!raw) return { icon: faGlobe, label: "Website", href: "" };
  let href = String(raw).trim();
  if (!/^https?:\/\//i.test(href)) href = `https://${href}`;
  try {
    const host = new URL(href).host.toLowerCase();
    if (host.includes("facebook.")) return { icon: faFacebook, label: "Facebook", href };
    if (host.includes("instagram.")) return { icon: faInstagram, label: "Instagram", href };
    if (host.includes("linkedin.") || host.includes("lnkd.in")) return { icon: faLinkedin, label: "LinkedIn", href };
    if (host.includes("youtube.") || host.includes("youtu.be")) return { icon: faYoutube, label: "YouTube", href };
  } catch { }
  return { icon: faGlobe, label: "Website", href };
};

/* ------------------------- COMPONENT ------------------------- */
export default function PrelegentEditorModal({
  open,
  onClose,
  secretId,
  segmentRow,            // { fullName, label } when called from SegmentBar
  onSaved,
  debug = true,
}) {
  const incomingFullMaybeShort = String(segmentRow?.segment_name || segmentRow?.fullName || segmentRow?.full || segmentRow || "").trim();

  // nazwy segmentu
  const [fullSeg, setFullSeg] = useState("");
  const [shortSeg, setShortSeg] = useState("");

  // form
  const [stageName, setStageName] = useState(""); // Nazwa segmentu (do edycji)
  const [name, setName] = useState("");
  const [info, setInfo] = useState("");
  const [link, setLink] = useState("");
  const [location, setLocation] = useState("");

  // media
  const [avatarUrl, setAvatarUrl] = useState("");
  const [bgUrl, setBgUrl] = useState(FALLBACK_BG);
  const [avatarFile, setAvatarFile] = useState(null);
  const [bgFile, setBgFile] = useState(null);
  const avatarFileRef = useRef(null);
  const bgFileRef = useRef(null);

  // ui
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  /* --------- Ustal short/full po otwarciu (z resolverem) --------- */
  useEffect(() => {
    let cancelled = false;
    if (!open) return;

    (async () => {
      const resolved = await resolveFullSegmentName(secretId, incomingFullMaybeShort);
      const full = resolved || incomingFullMaybeShort;
      const short = toShort(full, secretId);
      if (!cancelled) {
        setFullSeg(full);
        setShortSeg(short);
      }
      log("resolve full on open:", { incomingFullMaybeShort, resolved, using: full });
    })();

    return () => { cancelled = true; };
  }, [open, incomingFullMaybeShort, secretId]);

  const prettySegment = useMemo(() => stageName || shortSeg || "Prelegent", [stageName, shortSeg]);

  /* --------- ŁADOWANIE --------- */
  useEffect(() => {
    if (!open || !secretId || !fullSeg) return;

    const load = async () => {
      setLoading(true);
      setError("");
      setMsg("");
      setAvatarFile(null);
      setBgFile(null);
      if (avatarFileRef.current) avatarFileRef.current.value = "";
      if (bgFileRef.current) bgFileRef.current.value = "";

      // Initialize with props if available
      setStageName(shortSeg || "");
      if (segmentRow?.label) setStageName(segmentRow.label);

      try {
        const { res, json, text } = await fetchJson(`${API}/get-prelegent-info/${encodeURIComponent(secretId)}`);

        if (!res.ok) {
          const message = String(json?.message || json?.error || text || "");
          // Ignore specific legacy backend errors if acceptable
          if (!/Unknown column\s+'prelegent_bg'/i.test(message)) {
            // ...
          }
        }

        const rows = Array.isArray(json?.data) ? json.data : Array.isArray(json) ? json : [];
        const shortUnderscore = toShortUnderscore(fullSeg, secretId).toLowerCase();

        const row =
          rows.find((r) => (r?.segment_name || r?.segment) === fullSeg) ||
          rows.find((r) =>
            String(r?.segment_name || r?.segment || "").toLowerCase()
              .startsWith(`${shortUnderscore}_${String(secretId).toLowerCase()}_`)
          ) || null;

        if (debug) log("[load row]", { found: !!row, fullSeg });

        if (row) {
          // If backend has a stage_name, use it. Otherwise keep what we set from prop/short
          if (row.stage_name) setStageName(row.stage_name);

          setName(row.prelegent_name || "");
          setInfo(row.prelegent_info || "");
          setLink(row.prelegent_link || "");
          setLocation(row.prelegent_location || "");

          const img = normalizeMedia(row.prelegent_img || row["prelegent-img"] || "", "img");
          const bg = normalizeMedia(row.prelegent_bg || row["prelegent-bg"] || "", "bg");

          setAvatarUrl(img || "");
          setBgUrl(bg || FALLBACK_BG);
        }
      } catch (e) {
        setError(e?.message || "Nie udało się pobrać danych prelegenta.");
        err("LOAD ERROR", e);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [open, secretId, fullSeg, debug]);

  /* ------------------------- ZAPIS: funkcje przyjmują FULL ------------------------- */
  const saveAsFormData = async (fullName) => {
    const fd = new FormData();
    fd.append("secretId", secretId);
    fd.append("secred-id", secretId);
    fd.append("segment_name", fullName);
    fd.append("segment", fullName);
    fd.append("stage_name", stageName || "");
    fd.append("prelegent_name", name || "");
    fd.append("prelegent-info", info || "");
    fd.append("prelegent_info", info || "");
    fd.append("prelegent_link", link || "");
    fd.append("prelegent-location", location || "");
    fd.append("prelegent_location", location || "");
    const r = await fetchJson(`${API}/add-prelegent-info`, { method: "POST", body: fd });
    if (!r.res.ok || (r.json && r.json.success === false)) throw new Error(r.json?.message || r.text || "FormData rejected");
  };

  const saveAsUrlencoded = async (fullName) => {
    const p = new URLSearchParams();
    p.set("secretId", secretId);
    p.set("secred-id", secretId);
    p.set("segment_name", fullName);
    p.set("segment", fullName);
    p.set("stage_name", stageName || "");
    p.set("prelegent_name", name || "");
    p.set("prelegent-info", info || "");
    p.set("prelegent_info", info || "");
    p.set("prelegent_link", link || "");
    p.set("prelegent-location", location || "");
    p.set("prelegent_location", location || "");
    const r = await fetchJson(`${API}/add-prelegent-info`, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
      body: p.toString(),
    });
    if (!r.res.ok || (r.json && r.json.success === false)) throw new Error(r.json?.message || r.text || "Urlencoded rejected");
  };

  const saveAsJson = async (fullName) => {
    const payload = {
      secretId,
      "secred-id": secretId,
      segment_name: fullName,
      segment: fullName,
      stage_name: stageName || "",
      prelegent_name: name || "",
      "prelegent-info": info || "",
      prelegent_info: info || "",
      prelegent_link: link || "",
      "prelegent-location": location || "",
      prelegent_location: location || "",
    };
    const r = await fetchJson(`${API}/add-prelegent-info`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!r.res.ok || (r.json && r.json.success === false)) throw new Error(r.json?.message || r.text || "JSON rejected");
  };

  const uploadAvatarIfNeeded = async (fullName) => {
    if (!avatarFile) return;
    const fd = new FormData();
    fd.append("secred-id", secretId);
    fd.append("segment_name", fullName);
    fd.append("prelegent-img", avatarFile);
    const r = await fetchJson(`${API}/upload-prelegent-image`, { method: "POST", body: fd });
    if (!r.res.ok || !r.json?.success) throw new Error(r.json?.message || r.text || "Avatar upload failed");
  };

  const uploadBgIfNeeded = async (fullName) => {
    if (!bgFile) return;
    const fd = new FormData();
    fd.append("secred-id", secretId);
    fd.append("segment_name", fullName);
    fd.append("prelegent-bg", bgFile);
    const r = await fetchJson(`${API}/upload-prelegent-bg`, { method: "POST", body: fd });
    if (!r.res.ok || !r.json?.success) throw new Error(r.json?.message || r.text || "BG upload failed");
  };

  /* ------------------------- CLICK: Zapisz ------------------------- */
  const handleSave = async () => {
    console.group("%c[PEM] CLICK: Zapisz", "color:#7c3aed;font-weight:700;");
    console.time("[PEM] save-duration");
    console.log("inputs:", {
      secretId,
      fullSeg,
      stageName,
      name,
    });

    if (!secretId) {
      setError("Brak secretId – nie mogę zapisać.");
      console.groupEnd();
      return;
    }

    let fullForSave = fullSeg;
    if (!isFullLike(fullForSave, secretId)) {
      const resolved = await resolveFullSegmentName(secretId, fullSeg);
      if (resolved) {
        fullForSave = resolved;
        setFullSeg(resolved);
        setShortSeg(toShort(resolved, secretId));
      } else {
        setError("Nie mogę ustalić pełnej nazwy segmentu (brak stageId).");
        console.groupEnd(); return;
      }
    }

    try {
      setSaving(true);
      setError("");
      setMsg("");

      // 1) multipart/form-data
      try {
        await saveAsFormData(fullForSave);
      } catch (e1) {
        try {
          await saveAsUrlencoded(fullForSave);
        } catch (e2) {
          await saveAsJson(fullForSave);
        }
      }

      if (avatarFile) try { await uploadAvatarIfNeeded(fullForSave); } catch (e) { }
      if (bgFile) try { await uploadBgIfNeeded(fullForSave); } catch (e) { }

      setMsg("Zapisano.");
      onSaved?.();
      onClose?.();
    } catch (e) {
      setError(e?.message || "Coś poszło nie tak podczas zapisu.");
      console.error("SAVE ERROR:", e);
    } finally {
      setSaving(false);
      console.timeEnd("[PEM] save-duration");
      console.groupEnd();
    }
  };

  const canClickSave = !saving && !loading && !!secretId && !!fullSeg;
  if (!open) return null;
  const social = pickSocial(link);

  return (
    <div className="pem-overlay" role="dialog" aria-modal="true" style={{ zIndex: 9999 }}>
      <div className="pem-modal">
        {/* COVER */}
        <div className="pem-cover">
          <img src={bgUrl || FALLBACK_BG} alt="cover" onError={() => setBgUrl(FALLBACK_BG)} />
          <button className="pem-edit-btn" onClick={() => bgFileRef.current?.click()} type="button" aria-label="Edytuj tło">
            Edytuj tło
          </button>
          <input
            id="pem-bg"
            name="pem-bg"
            ref={bgFileRef}
            type="file"
            accept="image/*"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                setBgFile(f);
                setBgUrl(URL.createObjectURL(f));
              }
            }}
          />
        </div>

        {/* AVATAR */}
        <div className="pem-avatar-wrap">
          <div className="pem-avatar-inner">
            {avatarUrl ? (
              <img className="pem-avatar" src={avatarUrl} alt="prelegent" />
            ) : (
              <div className="pem-avatar-fallback">{(name?.trim()[0] || "?").toUpperCase()}</div>
            )}
            <button className="pem-edit-avatar" onClick={() => avatarFileRef.current?.click()} type="button" aria-label="Edytuj zdjęcie">
              Edytuj zdjęcie
            </button>
            <input
              id="pem-avatar"
              name="pem-avatar"
              ref={avatarFileRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) {
                  setAvatarFile(f);
                  setAvatarUrl(URL.createObjectURL(f));
                }
              }}
            />
          </div>
        </div>

        {/* FORM */}
        <div className="pem-content">
          <h2 className="pem-title">Edycja Segmentu</h2>

          {error && <p className="pem-msg pem-msg--error">{error}</p>}
          {msg && !error && <p className="pem-msg">{msg}</p>}
          {loading ? (
            <p className="pem-muted">Ładowanie danych…</p>
          ) : (
            <>
              {/* SEGMENT NAME INPUT */}
              <label className="pem-label" htmlFor="pem-stage-name">Nazwa segmentu</label>
              <input
                id="pem-stage-name"
                name="pem-stage-name"
                className="pem-input fw-bold"
                value={stageName}
                onChange={(e) => setStageName(e.target.value)}
                placeholder="Nazwa segmentu na liście"
                type="text"
              />

              <hr className="my-4 text-secondary opacity-25" />

              <label className="pem-label" htmlFor="pem-name">Imię i nazwisko prelegenta</label>
              <input id="pem-name" name="pem-name" className="pem-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="np. Jan Kowalski" type="text" autoComplete="name" />

              <label className="pem-label" htmlFor="pem-bio">Opis / Bio</label>
              <textarea id="pem-bio" name="pem-bio" className="pem-textarea" rows={3} value={info} onChange={(e) => setInfo(e.target.value)} placeholder="Krótki opis, stanowisko, tematyka…" />

              <label className="pem-label" htmlFor="pem-link">Link (opcjonalnie)</label>
              <div className="pem-link-row">
                <span className="pem-li" title={social.label}>
                  <FontAwesomeIcon icon={social.icon} />
                </span>
                <input
                  id="pem-link"
                  name="pem-link"
                  className="pem-input"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder="Wklej link do profilu lub strony"
                  type="url"
                  autoComplete="url"
                />
              </div>
            </>
          )}
        </div>

        {/* FOOTER */}
        <div className="pem-footer">
          <button className="pem-btn" onClick={onClose} disabled={saving}>Anuluj</button>
          <button
            type="button"
            className="pem-btn pem-btn--primary"
            onClick={handleSave}
            disabled={!canClickSave}
          >
            {saving ? "Zapisywanie…" : "Zapisz"}
          </button>
        </div>
      </div>

      <style>{`
        .pem-overlay{position:fixed;inset:0;background:rgba(0,0,0,.55);display:flex;align-items:center;justify-content:center;z-index:9999}
        .pem-modal{position:relative;width:min(760px,94vw);max-height:90vh;overflow-y:auto;background:#fff;border-radius:16px;box-shadow:0 20px 40px rgba(0,0,0,.28)}
        .pem-cover{position:relative;width:100%;height:200px;overflow:hidden;background:#f3f4f6}
        .pem-cover img{width:100%;height:100%;object-fit:cover;display:block}
        .pem-edit-btn{position:absolute;inset:auto 12px 12px auto;padding:8px 12px;border:0;border-radius:10px;background:#111827cc;color:#fff;opacity:0;transition:opacity .15s}
        .pem-cover:hover .pem-edit-btn{opacity:1}

        .pem-avatar-wrap{position:absolute;top:140px;left:24px}
        .pem-avatar-inner{position:relative;width:140px;height:140px;border-radius:50%;padding:4px;background:#fff;box-shadow:0 6px 20px rgba(0,0,0,.25)}
        .pem-avatar{width:100%;height:100%;border-radius:50%;object-fit:cover}
        .pem-avatar-fallback{width:100%;height:100%;border-radius:50%;display:grid;place-items:center;background:#e5e7eb;font-weight:700;font-size:36px;color:#111827}
        .pem-edit-avatar{position:absolute;left:50%;bottom:10px;transform:translateX(-50%);padding:6px 10px;border:0;border-radius:10px;background:#111827cc;color:#fff;opacity:0;transition:opacity .15s}
        .pem-avatar-inner:hover .pem-edit-avatar{opacity:1}

        .pem-content{padding:84px 20px 16px}
        .pem-title{margin:0 0 12px;font-size:22px;font-weight:800}
        .pem-label{display:block;font-weight:600;margin:12px 0 6px; font-size: 0.9rem; color: #374151;}
        .pem-input, .pem-textarea{width:100%;padding:10px 12px;border:1px solid #d1d5db;border-radius:8px; font-size: 0.95rem;}
        .pem-textarea{resize:vertical}
        .pem-link-row{display:flex;align-items:center;gap:8px}
        .pem-li{font-size:20px}
        .pem-muted{color:#6b7280;margin:8px 0}
        .pem-footer{display:flex;justify-content:flex-end;gap:10px;padding:12px 16px;border-top:1px solid #e5e7eb;background:#fafafa}
        .pem-btn{padding:10px 14px;border-radius:10px;border:1px solid #d1d5db;background:#fff;cursor:pointer; font-weight:600}
        .pem-btn--primary{background:#0d6efd;border-color:#0d6efd;color:#fff}
        .pem-msg{margin:8px 0 0;color:#10b981;font-weight:600}
        .pem-msg--error{color:#ef4444}
      `}</style>
    </div>
  );
}
